import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { useApiClient } from "../../api/ApiContext";
import {
  myRosteringSchema,
  rosterPlannerRevocationSchema,
  rosterPlannerSchema,
  rosterPlannersSchema,
  rosterQueryKeys,
  shiftTemplateSchema,
  shiftTemplatesSchema,
  staffingRequirementSchema,
  staffingRequirementsSchema,
} from "../../api/rosterSchemas";
import type {
  MyRostering,
  RosterPlanner,
  ShiftTemplate,
  StaffingRequirement,
} from "../../api/rosterSchemas";
import type { PlannerBody, RequirementBody, TemplateBody } from "./rosterModel";

/**
 * Loads whether rostering is active and what the person plans (`GET /me/rostering`).
 *
 * @param enabled - Whether the person has own records (every employee).
 * @returns The query result.
 */
export function useMyRostering(enabled: boolean): UseQueryResult<MyRostering> {
  const client = useApiClient();
  const query = useQuery({
    queryKey: rosterQueryKeys.mine,
    queryFn: ({ signal }) => client.get("/api/v1/me/rostering", myRosteringSchema, { signal }),
    enabled,
    staleTime: 300_000,
  });

  return query;
}

/**
 * Loads the shift templates the person sees.
 *
 * @param enabled - Whether the person plans or is HR.
 * @returns The query result.
 */
export function useShiftTemplates(enabled: boolean): UseQueryResult<ShiftTemplate[]> {
  const client = useApiClient();
  const query = useQuery({
    queryKey: rosterQueryKeys.templates,
    queryFn: ({ signal }) =>
      client.get("/api/v1/shift-templates", shiftTemplatesSchema, { signal }),
    enabled,
  });

  return query;
}

/**
 * Loads the staffing requirements the person sees.
 *
 * @param enabled - Whether the person plans or is HR.
 * @returns The query result.
 */
export function useStaffingRequirements(enabled: boolean): UseQueryResult<StaffingRequirement[]> {
  const client = useApiClient();
  const query = useQuery({
    queryKey: rosterQueryKeys.requirements,
    queryFn: ({ signal }) =>
      client.get("/api/v1/staffing-requirements", staffingRequirementsSchema, { signal }),
    enabled,
  });

  return query;
}

/**
 * Loads the grants of `PlanRoster` (HR only).
 *
 * @param enabled - Whether the person is HR.
 * @returns The query result.
 */
export function useRosterPlanners(enabled: boolean): UseQueryResult<RosterPlanner[]> {
  const client = useApiClient();
  const query = useQuery({
    queryKey: rosterQueryKeys.planners,
    queryFn: ({ signal }) =>
      client.get("/api/v1/roster-planners", rosterPlannersSchema, { signal }),
    enabled,
  });

  return query;
}

/** Input of {@link useSaveShiftTemplate}. */
export interface SaveTemplateInput {
  /** The template to change, or `null` to create one. */
  readonly id: string | null;
  readonly body: TemplateBody;
}

/**
 * Creates or changes a shift template.
 *
 * @returns The mutation.
 */
export function useSaveShiftTemplate(): UseMutationResult<ShiftTemplate, Error, SaveTemplateInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: SaveTemplateInput) =>
      input.id === null
        ? client.post("/api/v1/shift-templates", input.body, shiftTemplateSchema)
        : client.put(
            `/api/v1/shift-templates/${encodeURIComponent(input.id)}`,
            input.body,
            shiftTemplateSchema,
          ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: rosterQueryKeys.templates });
    },
  });

  return mutation;
}

/** Input of {@link useSaveStaffingRequirement}. */
export interface SaveRequirementInput {
  /** The requirement to change, or `null` to create one. */
  readonly id: string | null;
  readonly body: RequirementBody;
}

/**
 * Creates or changes a staffing requirement.
 *
 * @returns The mutation.
 */
export function useSaveStaffingRequirement(): UseMutationResult<
  StaffingRequirement,
  Error,
  SaveRequirementInput
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: SaveRequirementInput) =>
      input.id === null
        ? client.post("/api/v1/staffing-requirements", input.body, staffingRequirementSchema)
        : client.put(
            `/api/v1/staffing-requirements/${encodeURIComponent(input.id)}`,
            input.body,
            staffingRequirementSchema,
          ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: rosterQueryKeys.requirements });
    },
  });

  return mutation;
}

/**
 * Deletes a staffing requirement.
 *
 * @returns The mutation.
 */
export function useDeleteStaffingRequirement(): UseMutationResult<
  StaffingRequirement,
  Error,
  string
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (id: string) =>
      client.delete(
        `/api/v1/staffing-requirements/${encodeURIComponent(id)}`,
        staffingRequirementSchema,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: rosterQueryKeys.requirements });
    },
  });

  return mutation;
}

/**
 * Grants `PlanRoster` (HR).
 *
 * @returns The mutation.
 */
export function useGrantRosterPlanner(): UseMutationResult<RosterPlanner, Error, PlannerBody> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (body: PlannerBody) =>
      client.post("/api/v1/roster-planners", body, rosterPlannerSchema),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: rosterQueryKeys.planners });
      void queryClient.invalidateQueries({ queryKey: rosterQueryKeys.mine });
    },
  });

  return mutation;
}

/**
 * Ends a grant of `PlanRoster` (HR).
 *
 * @returns The mutation.
 */
export function useRevokeRosterPlanner(): UseMutationResult<
  { readonly removed: boolean },
  Error,
  string
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (id: string) =>
      client.delete(
        `/api/v1/roster-planners/${encodeURIComponent(id)}`,
        rosterPlannerRevocationSchema,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: rosterQueryKeys.planners });
      void queryClient.invalidateQueries({ queryKey: rosterQueryKeys.mine });
    },
  });

  return mutation;
}
